import { getAccountNumberError, getRoutingNumberError, isInternalUser } from '@steerfi/shared';
import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import { type ClientData, getClients } from '@/api/clients';
import {
  type CreateIndividualData,
  createIndividual,
  getSupplierAddConfig,
  type SupplierAddType,
} from '@/api/suppliers';
import {
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';
import { getFieldValidationError } from '@/utils/field-validators';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  FormControl,
  type FormErrorItem,
  FormErrorSummary,
  FormField,
  focusFirstInvalid,
} from './ui/form-field';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';

// ── Individual form types ───────────────────────────────────────────────

type PaymentMethod = 'ACH' | 'CHECK' | 'DIGITAL_WALLET';

interface IndividualFormData {
  firstName: string;
  lastName: string;
  email: string;
  caseId: string;
  street: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  paymentMethod: PaymentMethod | '';
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountType: 'CHECKING' | 'SAVINGS';
}

interface FormErrors {
  [key: string]: string;
}

const SUPPLIER_FIELD_LABELS: Record<string, string> = {
  client: 'Client',
  caseId: 'Case ID',
  firstName: 'First Name',
  lastName: 'Last Name',
  paymentMethod: 'Payment Method',
  email: 'Email',
  street: 'Street Address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP Code',
  bankRoutingNumber: 'Bank Routing Number',
  bankAccountNumber: 'Account Number',
  bizName: 'Business Name',
  bizEmail: 'Email',
  bizPaymentMethod: 'Payment Method',
};

function errorsToSummaryItems(errors: FormErrors, formError?: string | null): FormErrorItem[] {
  const items = Object.entries(errors).map(([key, message]) => ({
    label: SUPPLIER_FIELD_LABELS[key] ?? key,
    message,
  }));
  if (formError) {
    items.push({ label: 'Form', message: formError });
  }
  return items;
}

const INITIAL_INDIVIDUAL_FORM: IndividualFormData = {
  firstName: '',
  lastName: '',
  email: '',
  caseId: '',
  street: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  addressVerificationStatus: 'not_checked',
  paymentMethod: '',
  bankRoutingNumber: '',
  bankAccountNumber: '',
  bankAccountType: 'CHECKING',
};

// ── Component ───────────────────────────────────────────────────────────

interface SupplierAddModalProps {
  open: boolean;
  onClose: () => void;
  onAddSuccess?: () => void;
  partyId: string;
  /** When provided, reuses the same config as the page type filter (no fetch). */
  allowedTypes?: SupplierAddType[];
  /** Optional preselected client context for internal "on behalf of" workflows. */
  actingOnBehalfClient?: { id: string; name: string };
}

const DEFAULT_ALLOWED_TYPES: SupplierAddType[] = ['individual', 'business'];

export const SupplierAddModal: React.FC<SupplierAddModalProps> = ({
  open,
  onClose,
  onAddSuccess,
  partyId,
  allowedTypes: allowedTypesProp,
  actingOnBehalfClient,
}) => {
  const { labels } = useSupplierDisplay();
  const { user } = useKeycloakAuth();
  const isInternal = isInternalUser(user?.roles);
  const [allowedTypesState, setAllowedTypesState] =
    useState<SupplierAddType[]>(DEFAULT_ALLOWED_TYPES);
  const allowedTypes = allowedTypesProp?.length ? allowedTypesProp : allowedTypesState;
  const [supplierType, setSupplierType] = useState<SupplierAddType>('individual');

  // ── Client selector state (internal users only) ────────────────────────
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(actingOnBehalfClient?.id ?? '');
  const [clientsLoading, setClientsLoading] = useState(false);
  const isClientSelectionLocked = Boolean(actingOnBehalfClient);
  const effectiveSelectedClientId = actingOnBehalfClient?.id || selectedClientId;

  const filteredClients = useMemo(
    () =>
      clientSearch
        ? clients.filter((c) => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
        : clients,
    [clients, clientSearch]
  );

  // Fetch clients when modal opens for internal users
  useEffect(() => {
    if (!open || !isInternal || isClientSelectionLocked) return;
    setClientsLoading(true);
    getClients({ limit: 100, sortBy: 'name', order: 'asc' })
      .then((res) => setClients(res.data))
      .catch(() => setClients([]))
      .finally(() => setClientsLoading(false));
  }, [open, isInternal, isClientSelectionLocked]);

  useEffect(() => {
    if (actingOnBehalfClient?.id) {
      setSelectedClientId(actingOnBehalfClient.id);
    }
  }, [actingOnBehalfClient?.id]);

  // Fetch allowed types only when not provided by parent (e.g. when modal used outside suppliers page)
  useEffect(() => {
    if (allowedTypesProp != null || !open || !partyId) return;
    getSupplierAddConfig(partyId)
      .then((config) => {
        const types = config.allowedTypes?.length ? config.allowedTypes : DEFAULT_ALLOWED_TYPES;
        setAllowedTypesState(types);
        setSupplierType((current) => (types.includes(current) ? current : types[0]));
      })
      .catch(() => setAllowedTypesState(DEFAULT_ALLOWED_TYPES));
  }, [open, partyId, allowedTypesProp]);

  // Sync supplierType when allowedTypes (from prop or state) changes
  useEffect(() => {
    if (!allowedTypes.includes(supplierType)) {
      setSupplierType(allowedTypes[0]);
    }
  }, [allowedTypes, supplierType]);

  // ── Individual state ──────────────────────────────────────────────────
  const [indForm, setIndForm] = useState<IndividualFormData>(INITIAL_INDIVIDUAL_FORM);
  const [indErrors, setIndErrors] = useState<FormErrors>({});
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const formBodyRef = useRef<HTMLDivElement>(null);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  // ── Business state ────────────────────────────────────────────────────
  const [bizName, setBizName] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizPaymentMethod, setBizPaymentMethod] = useState('');
  const [bizDescription, setBizDescription] = useState('');
  const [bizErrors, setBizErrors] = useState<FormErrors>({});

  // ── Shared state ──────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Inline validation (runs on every keystroke) ─────────────────────
  const getIndFieldError = (field: keyof IndividualFormData, value: string): string | null =>
    getFieldValidationError(field, value, indForm.paymentMethod);

  // ── Individual helpers ────────────────────────────────────────────────
  const updateIndField = (field: keyof IndividualFormData, value: string | boolean) => {
    setIndForm((prev) => ({ ...prev, [field]: value }));
    if (
      indForm.addressVerificationStatus !== 'not_checked' &&
      'street address2 city state zipCode'.includes(String(field))
    ) {
      setIndForm((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    if (typeof field === 'string') {
      const error = getIndFieldError(field as keyof IndividualFormData, String(value));
      setIndErrors((prev) => {
        const next = { ...prev };
        if (error) {
          next[field] = error;
        } else {
          delete next[field];
        }
        return next;
      });
    }
  };

  const handleAddressChange = (
    field: 'address1' | 'address2' | 'city' | 'state' | 'zipCode',
    value: string
  ) => {
    const map: Record<string, keyof IndividualFormData> = {
      address1: 'street',
      address2: 'address2',
      city: 'city',
      state: 'state',
      zipCode: 'zipCode',
    };
    updateIndField(map[field], value);
  };

  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  const handleAcceptSuggestedAddress = (suggested: StandardizedAddress) => {
    setIndForm((prev) => ({
      ...prev,
      street: suggested.streetAddress,
      address2: suggested.secondaryAddress ?? '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);
  };

  const handleKeepOriginalAddress = () => {
    setIndForm((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validateIndividual = (): boolean => {
    const newErrors: FormErrors = {};

    if (isInternal && !effectiveSelectedClientId) {
      newErrors.client = 'Select a client from the list';
    }
    if (!indForm.caseId.trim()) newErrors.caseId = 'Enter a case ID';
    if (!indForm.firstName.trim()) {
      newErrors.firstName = 'Enter a first name';
    } else if (!/[a-zA-Z]/.test(indForm.firstName)) {
      newErrors.firstName = 'Enter a first name that includes at least one letter';
    }
    if (!indForm.lastName.trim()) {
      newErrors.lastName = 'Enter a last name';
    } else if (!/[a-zA-Z]/.test(indForm.lastName)) {
      newErrors.lastName = 'Enter a last name that includes at least one letter';
    }
    if (!indForm.paymentMethod) {
      newErrors.paymentMethod = 'Select a payment method';
    }

    if (!indForm.email.trim()) {
      newErrors.email = 'Enter an email like name@company.com';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(indForm.email)) {
      newErrors.email = 'Enter an email like name@company.com';
    }

    if (indForm.paymentMethod === 'ACH') {
      const routingError = getRoutingNumberError(indForm.bankRoutingNumber);
      if (routingError) newErrors.bankRoutingNumber = routingError;
      const accountError = getAccountNumberError(indForm.bankAccountNumber);
      if (accountError) newErrors.bankAccountNumber = accountError;
    }

    if (!indForm.street.trim()) {
      newErrors.street = 'Enter a street address';
    } else if (indForm.street.includes('@')) {
      newErrors.street = 'Enter a street address without an @ symbol';
    }
    if (!indForm.city.trim()) {
      newErrors.city = 'Enter a city';
    } else if (!/[a-zA-Z]/.test(indForm.city)) {
      newErrors.city = 'Enter a city name that includes at least one letter';
    }
    if (!indForm.state) newErrors.state = 'Select a state';
    if (!indForm.zipCode.trim()) {
      newErrors.zipCode = 'Enter a ZIP code';
    } else if (!/^\d{5}(-\d{4})?$/.test(indForm.zipCode.trim())) {
      newErrors.zipCode = 'Enter a 5-digit ZIP or ZIP+4 like 10001-1234';
    }

    setIndErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBusiness = (): boolean => {
    const newErrors: FormErrors = {};
    if (!bizName.trim()) newErrors.bizName = 'Enter a business name';
    if (!bizEmail.trim()) {
      newErrors.bizEmail = 'Enter an email like name@company.com';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(bizEmail)) {
      newErrors.bizEmail = 'Enter an email like name@company.com';
    }
    if (!bizPaymentMethod) newErrors.bizPaymentMethod = 'Select a payment method';
    setBizErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Reset ─────────────────────────────────────────────────────────────
  const resetForm = () => {
    setSupplierType(allowedTypes[0] ?? 'individual');
    setIndForm(INITIAL_INDIVIDUAL_FORM);
    setIndErrors({});
    setShowAddressDialog(false);
    setAddressVerificationResult(null);
    setBizName('');
    setBizEmail('');
    setBizPaymentMethod('');
    setBizDescription('');
    setBizErrors({});
    setSelectedClientId(actingOnBehalfClient?.id ?? '');
    setClientSearch('');
    setError(null);
    setSubmitAttempt(0);
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    setError(null);

    if (supplierType === 'individual') {
      if (!validateIndividual()) {
        setSubmitAttempt((n) => n + 1);
        return;
      }

      setIsSubmitting(true);
      try {
        const payload: CreateIndividualData = {
          first_name: indForm.firstName.trim(),
          last_name: indForm.lastName.trim(),
          email: indForm.email.trim(),
          case_id: indForm.caseId.trim(),
          payment_method: indForm.paymentMethod as PaymentMethod,
          street: indForm.street.trim(),
          city: indForm.city.trim(),
          state: indForm.state,
          zip_code: indForm.zipCode.trim(),
        };

        const line2 = indForm.address2.trim();
        if (line2) {
          payload.address2 = line2;
        }

        if (isInternal && effectiveSelectedClientId) {
          payload.party_id = effectiveSelectedClientId;
        }

        payload.address_verification_status = indForm.addressVerificationStatus;

        if (indForm.paymentMethod === 'ACH') {
          payload.bank_routing_number = indForm.bankRoutingNumber.trim();
          payload.bank_account_number = indForm.bankAccountNumber.trim();
          payload.bank_account_type = indForm.bankAccountType;
        }

        await createIndividual(payload);

        toast.success(`${labels.singular} created successfully`, {
          description: `${indForm.firstName} ${indForm.lastName} has been added as a ${labels.singular.toLowerCase()}.`,
        });
        resetForm();
        onAddSuccess?.();
        onClose();
      } catch (err: unknown) {
        const rawMessage = err instanceof Error ? err.message : 'Failed to create individual';
        if (/case id.*already been created/i.test(rawMessage)) {
          const caseIdError = 'Enter a different case ID. That one has already been created.';
          setIndErrors((prev) => ({ ...prev, caseId: caseIdError }));
          setSubmitAttempt((n) => n + 1);
          toast.error(`Failed to create ${labels.singular.toLowerCase()}`, {
            description: caseIdError,
          });
        } else {
          setError(rawMessage);
          setSubmitAttempt((n) => n + 1);
          toast.error(`Failed to create ${labels.singular.toLowerCase()}`, {
            description: rawMessage,
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!validateBusiness()) {
        setSubmitAttempt((n) => n + 1);
        return;
      }
      toast.info(`Business ${labels.singular.toLowerCase()} creation is not yet implemented.`);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const isClientSelected = !isInternal || !!effectiveSelectedClientId;
  const isIndividualValid =
    isClientSelected &&
    indForm.caseId.trim() &&
    indForm.firstName.trim() &&
    indForm.lastName.trim() &&
    indForm.email.trim() &&
    indForm.paymentMethod &&
    indForm.street.trim() &&
    indForm.city.trim() &&
    indForm.state &&
    indForm.zipCode.trim() &&
    (indForm.paymentMethod !== 'ACH' ||
      (indForm.bankRoutingNumber.trim() && indForm.bankAccountNumber.trim()));
  const isBusinessValid = bizName.trim() && bizEmail.trim() && bizPaymentMethod;
  const canSubmit = supplierType === 'individual' ? isIndividualValid : isBusinessValid;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] flex flex-col gap-0 p-0">
        {/* ── Sticky header ──────────────────────────────────────────── */}
        <div className="shrink-0 border-b px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle>Add {labels.singular}</DialogTitle>
            <DialogDescription>
              Enter the {labels.singular.toLowerCase()}&apos;s details below to add them to your
              account.
            </DialogDescription>
          </DialogHeader>

          {allowedTypes.length > 1 ? (
            <Tabs
              value={supplierType}
              onValueChange={(v) => setSupplierType(v as SupplierAddType)}
              className="mt-4"
            >
              <TabsList className="grid w-full grid-cols-2">
                {allowedTypes.includes('individual') && (
                  <TabsTrigger value="individual">{labels.singular}</TabsTrigger>
                )}
                {allowedTypes.includes('business') && (
                  <TabsTrigger value="business">Business</TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          ) : null}
        </div>

        {/* ── Scrollable body ────────────────────────────────────────── */}
        <div ref={formBodyRef} className="flex-1 overflow-y-auto px-6 py-5">
          <FormErrorSummary
            errors={errorsToSummaryItems(
              supplierType === 'individual' ? indErrors : bizErrors,
              error
            )}
            attempt={submitAttempt}
            onAnnounced={() => focusFirstInvalid(formBodyRef.current)}
          />
          {supplierType === 'individual' ? (
            <div className="space-y-5">
              {/* Client selector for internal users */}
              {isInternal && (
                <>
                  <SectionHeader first>Client</SectionHeader>
                  {isClientSelectionLocked ? (
                    <FormField label="Client" required error={indErrors.client}>
                      <FormControl>
                        <Input value={actingOnBehalfClient?.name ?? ''} disabled readOnly />
                      </FormControl>
                    </FormField>
                  ) : (
                    <FormField label="Client" required error={indErrors.client}>
                      <Select
                        value={selectedClientId}
                        onValueChange={(v) => {
                          setSelectedClientId(v);
                          if (indErrors.client) {
                            setIndErrors((prev) => {
                              const next = { ...prev };
                              delete next.client;
                              return next;
                            });
                          }
                        }}
                        disabled={isSubmitting || clientsLoading}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={
                                clientsLoading ? 'Loading clients...' : 'Select a client'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent onCloseAutoFocus={() => setClientSearch('')}>
                          <div className="px-2 pb-1.5">
                            <Input
                              placeholder="Search clients..."
                              value={clientSearch}
                              onChange={(e) => setClientSearch(e.target.value)}
                              className="h-8"
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredClients.length === 0 ? (
                            <div className="py-2 px-2 text-sm text-muted-foreground">
                              No clients found
                            </div>
                          ) : (
                            filteredClients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormField>
                  )}
                </>
              )}

              {/* Individual Information — Case ID at top */}
              <SectionHeader first={!isInternal}>{labels.singular} Information</SectionHeader>
              <FormField label="Case ID" required error={indErrors.caseId}>
                <FormControl>
                  <Input
                    placeholder="Case ID"
                    value={indForm.caseId}
                    onChange={(e) =>
                      updateIndField(
                        'caseId',
                        e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
                      )
                    }
                    disabled={isSubmitting}
                  />
                </FormControl>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="First Name" required error={indErrors.firstName}>
                  <FormControl>
                    <Input
                      placeholder="First Name"
                      value={indForm.firstName}
                      onChange={(e) => updateIndField('firstName', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                </FormField>
                <FormField label="Last Name" required error={indErrors.lastName}>
                  <FormControl>
                    <Input
                      placeholder="Last Name"
                      value={indForm.lastName}
                      onChange={(e) => updateIndField('lastName', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                </FormField>
              </div>

              {/* Payment Method */}
              <FormField label="Payment Method" required error={indErrors.paymentMethod}>
                <Select
                  value={indForm.paymentMethod}
                  onValueChange={(v) => updateIndField('paymentMethod', v)}
                  disabled={isSubmitting}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ACH">ACH</SelectItem>
                    <SelectItem value="CHECK">Check</SelectItem>
                    <SelectItem value="DIGITAL_WALLET">Digital Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              {/* ACH Bank Details */}
              {indForm.paymentMethod === 'ACH' && (
                <div className="space-y-4 rounded-md border p-4 bg-muted/30">
                  <h4 className="text-sm font-semibold text-foreground">Bank Account Details</h4>
                  <FormField label="Account Type">
                    <Select
                      value={indForm.bankAccountType}
                      onValueChange={(v) =>
                        updateIndField('bankAccountType', v as 'CHECKING' | 'SAVINGS')
                      }
                      disabled={isSubmitting}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="CHECKING">Checking</SelectItem>
                        <SelectItem value="SAVINGS">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      label="Bank Routing Number"
                      required
                      error={indErrors.bankRoutingNumber}
                    >
                      <FormControl>
                        <Input
                          placeholder="9-digit routing number"
                          value={indForm.bankRoutingNumber}
                          onChange={(e) =>
                            updateIndField('bankRoutingNumber', e.target.value.replace(/\D/g, ''))
                          }
                          disabled={isSubmitting}
                          maxLength={9}
                        />
                      </FormControl>
                    </FormField>
                    <FormField label="Account Number" required error={indErrors.bankAccountNumber}>
                      <FormControl>
                        <Input
                          placeholder="Account number"
                          value={indForm.bankAccountNumber}
                          onChange={(e) =>
                            updateIndField('bankAccountNumber', e.target.value.replace(/\D/g, ''))
                          }
                          disabled={isSubmitting}
                          maxLength={17}
                        />
                      </FormControl>
                    </FormField>
                  </div>
                </div>
              )}

              <SectionHeader>Additional Details</SectionHeader>
              <AddressFormFields
                address={{
                  address1: indForm.street,
                  address2: indForm.address2,
                  city: indForm.city,
                  state: indForm.state,
                  zipCode: indForm.zipCode,
                }}
                errors={{
                  address1: indErrors.street,
                  address2: indErrors.address2,
                  city: indErrors.city,
                  state: indErrors.state,
                  zipCode: indErrors.zipCode,
                }}
                onChange={handleAddressChange}
                onVerificationResult={handleAddressVerificationResult}
                onVerificationStatusChange={(status) =>
                  setIndForm((prev) => ({ ...prev, addressVerificationStatus: status }))
                }
                disabled={isSubmitting}
              />
              <FormField label="Email" required error={indErrors.email}>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={indForm.email}
                    onChange={(e) => updateIndField('email', e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormControl>
              </FormField>
            </div>
          ) : (
            /* ── Business form ──────────────────────────────────────── */
            <div className="space-y-4">
              <FormField label="Business Name" required error={bizErrors.bizName}>
                <FormControl>
                  <Input
                    placeholder="Company name"
                    value={bizName}
                    onChange={(e) => setBizName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormControl>
              </FormField>
              <FormField label="Email" required error={bizErrors.bizEmail}>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="business@example.com"
                    value={bizEmail}
                    onChange={(e) => setBizEmail(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormControl>
              </FormField>
              <FormField label="Payment Method" required error={bizErrors.bizPaymentMethod}>
                <Select
                  value={bizPaymentMethod}
                  onValueChange={setBizPaymentMethod}
                  disabled={isSubmitting}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="virtual_wallet">Virtual Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Description (Optional)">
                <FormControl>
                  <Textarea
                    placeholder="Add notes or description about this business..."
                    value={bizDescription}
                    onChange={(e) => setBizDescription(e.target.value)}
                    disabled={isSubmitting}
                    rows={3}
                  />
                </FormControl>
              </FormField>
            </div>
          )}
        </div>

        {/* ── Sticky footer ──────────────────────────────────────────── */}
        <div className="shrink-0 border-t px-6 py-5 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  Failed to add {labels.singular.toLowerCase()}
                </p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleAdd} disabled={!canSubmit || isSubmitting}>
              {isSubmitting
                ? 'Adding...'
                : supplierType === 'individual'
                  ? `Create ${labels.singular}`
                  : `Add ${labels.singular}`}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
      <AddressVerificationDialog
        open={showAddressDialog}
        onOpenChange={setShowAddressDialog}
        verificationResult={addressVerificationResult}
        onAcceptSuggested={handleAcceptSuggestedAddress}
        onKeepOriginal={handleKeepOriginalAddress}
      />
    </Dialog>
  );
};

// ── Helper components ───────────────────────────────────────────────────

const SectionHeader: React.FC<{ children: React.ReactNode; first?: boolean }> = ({
  children,
  first,
}) => (
  <div className={first ? '' : 'border-t pt-5'}>
    <h3 className="text-lg font-semibold tracking-tight text-foreground">{children}</h3>
  </div>
);
